import React, { useState, useEffect } from 'react'
import { makeStyles } from '@mui/styles'
import { formatCurrency } from '@/modules/_helpers/formatCurrency'
import lang from '@/lang'
import config from '@/config'
import EconomicAssetsTableIcon from '@assets/images/economicAssetsTableIcon.svg'
import LowValueIcon from '@assets/images/lowValueIcon.svg'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import PerfectScrollbar from 'react-perfect-scrollbar'
import { exportToCSV } from '../_helpers/getExcelExport'
import IconDownload from '@assets/images/icons/downloadExcel.svg'
import moment from 'moment'
import PropTypes from 'prop-types'
import { getSpecialDepreciationTypeLabel } from '@/modules/economicAsset/constants'
import RichTooltip from '@/modules/base/components/rich-tooltip/rich-tooltip'
import clsx from 'clsx'
const { dateFormat: { client: clientDateFormat } } = config
const useStyles = makeStyles(theme => ({
  tableContainer: {
    maxWidth: 800,
    minWidth: 800,
    paddingLeft: 30,
    paddingRight: 30,
    height: '100%',
    paddingBottom: 30,
  },
  iconContainer: {
    maxWidth: 800,
    minWidth: 800,
    overflowX: 'auto',
    paddingLeft: 30,
    paddingRight: 30,
    height: '100%',
    display: 'flex',
  },
  iconWrapper: {
    display: 'flex',
    flexDirection: 'column',
    textAlign: 'center',
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    fontWeight: theme.fontWeights.semiBold,
  },
  scrollableTable: {
    borderCollapse: 'collapse',
    backgroundColor: theme.white,
    borderBottom: theme.border,
    borderLeft: theme.border,
    borderRight: theme.border,

  },
  table: {
    width: '100%',
    borderSpacing: 0,
  },
  cell: {
    borderTop: theme.border,
    padding: 10,
    textAlign: 'center',
  },
  clickableCell: {
    cursor: 'pointer',
  },
  cellDisabled: {
    borderTop: theme.border,
    padding: 10,
    textAlign: 'center',
    color: theme.disabledColor,
  },
  cellWithBackground: {
    borderTop: theme.border,
    padding: 10,
    textAlign: 'center',
    backgroundColor: theme.rowHoverColor,
  },
  additionalInfo: {
    marginTop: 10,
    display: 'flex',
    flexDirection: 'column',
    '& span': {
      fontSize: theme.fontSizes.smaller,
    },
    paddingBottom: 10,
  },
  additionalSubtitle: {
    fontWeight: theme.fontWeights.medium,
  },
  exportButton: {
    paddingTop: 10,
  },
  downloadIcon: {
    paddingLeft: 5,
  },
  infoRowWrapper: {
    display: 'flex',
    justifyContent: 'flex-start',
    gap: 20,
  },
  infoSecondBlock: {
  },
  specialDepreciationTooltip: {
    fontSize: theme.fontSizes.smaller,
  },
}))

const EconomicAssetTable = (props) => {
  const [combinedData, setCombinedData] = useState([])
  const {
    economicData,
    depreciationMethod,
    costOfPurchase,
    economicLife,
    economicAssetData,
    description,
    number,
    dateOfPurchase,
    periodType,
    isSpecialDepreciation,
    specialDepreciationData: { specialDepreciation, specialDepreciationComment, specialDepreciationDoneOn },
    isGWG,
    possibleDepreciationMethods,
    onHeaderClick,
  } = props
  const classes = useStyles()
  const comment = specialDepreciationComment || ''
  const truncatedSpecialDepreciationComment = comment.length > 15 ? comment.substring(0, 15) + '...' : comment
  const shortLabel = possibleDepreciationMethods?.find(method => method.key === depreciationMethod)?.shortLabel || depreciationMethod
  const label = possibleDepreciationMethods?.find(method => method.key === depreciationMethod)?.label || depreciationMethod
  useEffect(() => {
    const combinedData = economicData.reduce((resultingTable, item) => {
      return item.annualDepreciationRecords.reduce((table, record, index) => {
        const data = {
          [`${item.depreciationMethod}DepreciationAmount`]: record.depreciationAmount,
          [`${item.depreciationMethod}AccumulatedDepreciationAmount`]: record.accumulatedDepreciationAmount,
          [`${item.depreciationMethod}EndingBookValue`]: record.endingBookValue,
        }
        if (!table[index]) {
          table[index] = {
            ...data,
            year: record.year,
          }
        } else {
          table[index] = {
            ...table[index],
            ...data,
          }
        }
        return table
      }, resultingTable)
    }, [])
    setCombinedData(combinedData)
  }, [economicData, depreciationMethod])

  const exportExcel = () => {
    exportToCSV(economicAssetData, possibleDepreciationMethods?.map(method => method.label), description, number)
  }

  const handleClick = (methodKey) => {
    // call parent onHeaderClick
    onHeaderClick(methodKey)
  }

  const additionalInfo = (
    economicAssetData?.length && !isGWG
      ? (<div className={classes.infoRowWrapper}>
        <div className={clsx('flex-grow', classes.infoSecondBlock)}>
          <div>
            <span className={classes.additionalSubtitle}>{lang.economicAsset.table.additionalInfoDescription}</span>
            <span> {description}</span>
          </div>
          <div>
            <span className={classes.additionalSubtitle}>{lang.economicAsset.table.additionalInfoPeriodType}</span>
            <span> {periodType}</span>
          </div>
          <div>
            <span className={classes.additionalSubtitle}>{lang.economicAsset.table.additionalInfoInventoryNumber}</span>
            <span> {number}</span>
          </div>
        </div>
        <div className={classes.infoSecondBlock}>
          <div>
            <span className={classes.additionalSubtitle}>{lang.economicAsset.table.additionalInfoDateOfPurchase}</span>
            <span> {moment(dateOfPurchase).format(clientDateFormat)}</span>
          </div>
          <div>
            <span className={classes.additionalSubtitle}>{lang.economicAsset.table.additionalInfoTime}</span>
            <span> {economicLife} {lang.economicAsset.table.additionalInfoYear}</span>
          </div>
          <div>
            <span className={classes.additionalSubtitle}>{lang.economicAsset.table.additionalInfoCostOfPurchase}</span>
            <span> {formatCurrency(costOfPurchase)}</span>
          </div>
        </div>
        {isSpecialDepreciation
          ? <div className={classes.infoSecondBlock}>
            <div>
              <span className={classes.additionalSubtitle}>{lang.economicAsset.table.specialDepreciationType}</span>
              <span> {getSpecialDepreciationTypeLabel(specialDepreciation)}</span>
            </div>
            <div>
              <span className={classes.additionalSubtitle}>{lang.economicAsset.table.specialDepreciationDate}</span>
              <span> {moment(specialDepreciationDoneOn).format(clientDateFormat)}</span>
            </div>
            <div>
              <span className={classes.additionalSubtitle}>{lang.economicAsset.table.specialDepreciationComment}</span>
              <RichTooltip content={<div className={classes.specialDepreciationTooltip}>{specialDepreciationComment}</div>}>
                <span> {truncatedSpecialDepreciationComment}</span>
              </RichTooltip>
            </div>
          </div>
          : null}
      </div>)
      : (
        <div>
          <div>
            <span className={classes.additionalSubtitle}>{lang.economicAsset.table.additionalInfoMethod}</span>
            <span> {label}</span>
          </div>
          <div>
            <span className={classes.additionalSubtitle}>{lang.economicAsset.table.additionalInfoTime}</span>
            <span> {economicLife} {lang.economicAsset.table.additionalInfoYear}</span>
          </div>
          <div>
            <span className={classes.additionalSubtitle}>{lang.economicAsset.table.additionalInfoCostOfPurchase}</span>
            <span> {formatCurrency(costOfPurchase)}</span>
          </div>
        </div>
      )
  )
  const tableHeader = possibleDepreciationMethods?.reduce((headers, method) => {
    headers.push(<th
      className={clsx(classes.cell, classes.clickableCell)}
      onClick={() => handleClick(method.key)}>{'AfA ' + method.shortLabel}</th>)
    return headers
  }, [<th className={classes.cell}>{lang.economicAsset.table.cell1}</th>]) || []
  tableHeader.push(<th className={classes.cell}>{lang.economicAsset.table.cell4}</th>)
  tableHeader.push(<th className={classes.cell}>{lang.economicAsset.table.cell5}</th>)
  const selectedIndex = possibleDepreciationMethods?.findIndex(method => method.key === depreciationMethod)

  const tableBody = combinedData?.flat().map((data, index) => {
    const methodColums = possibleDepreciationMethods?.map((method, methodIndex) => {
      return (
        <td className={selectedIndex === methodIndex ? classes.cellWithBackground : classes.cellDisabled}>{data[`${method.key}DepreciationAmount`] === undefined ? '-' : formatCurrency(data[`${method.key}DepreciationAmount`])}</td>
      )
    })
    const accumulatedDepreciationAmount = data[`${possibleDepreciationMethods[selectedIndex]?.key}AccumulatedDepreciationAmount`]
    const endingBookValue = data[`${possibleDepreciationMethods[selectedIndex]?.key}EndingBookValue`]
    return (
      <tr key={index + 1}>
        <td className={classes.cell}>{data.year}</td>
        {methodColums}
        <td className={classes.cell}>{endingBookValue === undefined ? '-' : formatCurrency(accumulatedDepreciationAmount)}</td>
        <td className={classes.cell}>{endingBookValue === undefined ? '-' : formatCurrency(endingBookValue)}</td>
      </tr>
    )
  })
  return (
    (combinedData?.length || economicAssetData?.length) && !isGWG
      ? <PerfectScrollbar className={classes.tableContainer}>
        <p className={classes.additionalInfo}>
          {additionalInfo}
        </p>

        <div className={classes.scrollableTable}>
          <table className={classes.table}>
            <thead>
              {
                economicAssetData.length
                  ? <tr>
                    <th className={classes.cell}>{lang.economicAsset.table.cell1}</th>
                    <th className={classes.cell}>{'AFA ' + shortLabel}</th>
                    <th className={classes.cell}>{lang.economicAsset.table.cell4}</th>
                    <th className={classes.cell}>{lang.economicAsset.table.cell5}</th>
                  </tr>
                  : <tr>
                    {tableHeader}
                  </tr>
              }
            </thead>
            <tbody>
              {
                economicAssetData.length
                  ? economicAssetData?.flat().map((data, index) => (
                    <tr key={index + 1}>
                      <td className={(new Date().getFullYear() === data.year) ? classes.cellWithBackground : classes.cell}>{data.year}</td>
                      <td className={(new Date().getFullYear() === data.year) ? classes.cellWithBackground : classes.cell}>{formatCurrency(data.depreciationAmount)}</td>
                      <td className={(new Date().getFullYear() === data.year) ? classes.cellWithBackground : classes.cell}>{formatCurrency(data.accumulatedDepreciationAmount)}</td>
                      <td className={(new Date().getFullYear() === data.year) ? classes.cellWithBackground : classes.cell}>{formatCurrency(data.endingBookValue)}</td>
                    </tr>
                  ))
                  : tableBody
              }
            </tbody>
          </table>
        </div>
        {
          economicAssetData.length
            ? <div className={classes.exportButton}>
              <a onClick={exportExcel}>{
                lang.economicAsset.preview.exportButtonTitle} <SVGIcon className={classes.downloadIcon} svg={IconDownload} width={'12px'} height={'12px'} />
              </a>

            </div>
            : null
        }

      </PerfectScrollbar>
      : <div className={classes.iconContainer}>
        <span className={classes.iconWrapper}>
          <SVGIcon
            className={classes.arrowIcon}
            svg={isGWG ? LowValueIcon : EconomicAssetsTableIcon}
            width={'100px'}
            height={'130px'}
          />
          <span>{isGWG ? lang.economicAsset.preview.emptyLowValueDescription1 : lang.economicAsset.preview.emptyStateDescription1}</span>
          <span>{isGWG ? lang.economicAsset.preview.emptyLowValueDescription2 : lang.economicAsset.preview.emptyStateDescription2}</span>
        </span>
      </div>

  )

}

EconomicAssetTable.propTypes = {
  economicData: PropTypes.shape({
    linear: PropTypes.arrayOf(PropTypes.object),
    decliningBalance: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  depreciationMethod: PropTypes.string.isRequired,
  costOfPurchase: PropTypes.number.isRequired,
  economicLife: PropTypes.string.isRequired,
  economicAssetData: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.object)).isRequired,
  description: PropTypes.string.isRequired,
  number: PropTypes.string.isRequired,
  dateOfPurchase: PropTypes.string.isRequired,
  periodType: PropTypes.string.isRequired,
}

export default EconomicAssetTable
